import { UserTypeCustomer } from "@dana/Common/Constants";
import useIsNA from "@dana/Common/Hooks/useIsNA";
import colorPallete, { bodyFonts, buttonFonts, elevation, headlineFonts } from "@dana/MobiusOverrides/styles/variables";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import postBreadthRebateProgram from "@dana/Store/Data/BreadthRebateProgram/Handlers/PostBreadthRebateProgramHandler";
import { SoldToAddress } from "@dana/Types/ApiModels";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";


const mapStateToProps = (state: ApplicationStateDana) => {

    const { isAuthenticated, rememberMe, isGuest, properties } = state.context.session;
    const userType = properties?.userType ?? "";
    const danaSettings = getDanaGeneralSettings(state);
    const danaBreadthRebateProgramFile = danaSettings?.danaBreadthRebateProgramFile;
    const isNARegion = useIsNA(state);
    
    const soldto = state.breadthRebateProgram.data?.updatedSoldTo ? state.breadthRebateProgram.data?.updatedSoldTo :
        properties?.soldToAddress &&
            properties?.soldToAddress !== "" &&
            properties?.soldToAddress !== undefined
            ? (JSON.parse(properties?.soldToAddress) as SoldToAddress)
            : undefined;

    return {
        isAuthenticated,
        isNARegion,
        userType,
        soldto,
        isSubmitting: state.breadthRebateProgram.isLoading,
        isSuccess: state.breadthRebateProgram.data?.isSuccess,
        currentLocation: getLocation(state),
        danaBreadthRebateProgramFile
    };
};

const mapDispatchToProps = {
    postBreadthRebateProgram,
};

type Props =
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;
export interface BreadthRebateProgramPopupStyles {
    modal?: ModalPresentationProps;
    headlineText?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    detailsLink?: ClickableProps;
    buttonsWrapper?: InjectableCss;
    primaryButton?: ButtonPresentationProps;
    secondaryButton?: ButtonPresentationProps;
}

export const breadthRebateProgramPopupStyles: BreadthRebateProgramPopupStyles = {
    modal: {
        cssOverrides: {
            modalBody: css`
                border-radius: 0;
                box-shadow: ${elevation.dp16};
            `,
            modalTitle: css`
                position: relative;
                justify-content: center;
                text-align: center;
                border-bottom: none;
                padding: 40px 48px 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 56px 24px 0;
                        `,
                        null,
                        null,
                        null,
                    ], "min")}
            `,
            modalContent: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                padding: 16px 48px 40px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 16px 24px 32px;
                        `,
                        null,
                        null,
                        null,
                    ], "min")}
            `,
            titleButton: css`
                position: absolute;
                top: 12px;
                right: 12px;
                border: none;
                background: transparent;
                color: ${colorPallete.black90};
            `,
        },
    },
    headlineText: {
        css: css`
            display: block;
            margin: 0;
            color: ${colorPallete.black90};
            font-family: ${headlineFonts.headline3SemiBoldLg.fontFamily};
            font-size: ${headlineFonts.headline3SemiBoldLg.fontSize};
            line-height: ${headlineFonts.headline3SemiBoldLg.lineHeight};
            font-weight: ${headlineFonts.headline3SemiBoldLg.fontWeight};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${headlineFonts.headline3SemiBoldSm.fontSize};
                        line-height: ${headlineFonts.headline3SemiBoldSm.lineHeight};
                    `,
                    null,
                    null,
                    null,
                ], "min")}
        `,
    },
    descriptionText: {
        css: css`
            margin: 0 0 16px;
            color: ${colorPallete.black90};
            font-family: ${bodyFonts.body1RegularLg.fontFamily};
            font-size: ${bodyFonts.body1RegularLg.fontSize};
            line-height: ${bodyFonts.body1RegularLg.lineHeight};
            font-weight: ${bodyFonts.body1RegularLg.fontWeight};
        `,
    },
    detailsLink: {
        css: css`
            margin-bottom: 32px;
            font-family: ${bodyFonts.body1RegularLg.fontFamily};
            font-size: ${bodyFonts.body1RegularLg.fontSize};
            line-height: ${bodyFonts.body1RegularLg.lineHeight};
             color: ${colorPallete.semanticBlue500};
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 16px;
            width: 100%;
            max-width: 324px;
        `,
    },
    primaryButton: {
        css: css`
            width: 100%;
            padding: 14px 24px;
            font-size: ${buttonFonts.buttonLg.fontSize};
            line-height: ${buttonFonts.buttonLg.lineHeight};
            font-weight: ${buttonFonts.buttonLg.fontWeight};
        `,
    },
    secondaryButton: {
        css: css`
            width: 100%;
            padding: 14px 24px;
            background: ${colorPallete.white};
            font-size: ${buttonFonts.buttonLg.fontSize};
            line-height: ${buttonFonts.buttonLg.lineHeight};
            font-weight: ${buttonFonts.buttonLg.fontWeight};
        `,
    },
};

const styles = breadthRebateProgramPopupStyles;

const BreadthRebateProgram = ({
    isAuthenticated,
    isNARegion,
    soldto,
    userType,
    currentLocation,
    isSubmitting,
    postBreadthRebateProgram,
    danaBreadthRebateProgramFile,
    isSuccess
}: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenSuccess, setIsOpenSuccess] = useState(false);
    const toasterContext = useContext(ToasterContext);

    useEffect(() => {
        if (isNARegion &&
            isAuthenticated &&
            userType === UserTypeCustomer &&
            soldto?.IsBreadthRebateEligible === "true" &&
            soldto?.BreadthRebateStatus === "Not Complete" &&
            currentLocation.pathname !== "/SignIn" && !isSuccess) {
            setIsOpen(true);
        }
    }, [isAuthenticated, isNARegion, soldto?.BreadthRebateStatus]);

    const acceptHandler = () => {
        postBreadthRebateProgram({
            action: "Accept",
            soldToId: soldto?.Id,
            onSuccess: () => {
                setIsOpen(false);
                setIsOpenSuccess(false);
                toasterContext.addToast({
                    body: translate("Breadth Rebate Program activated successfully."),
                    messageType: "success",
                    isFocusAfterMount: true,
                });
            },
            onError: showErrorToast,
        });

    };
    // The success modal opens only once the backend has accepted the program, never before the click.
    const showErrorToast = () => {
        setIsOpen(false);
        setIsOpenSuccess(false);
        toasterContext.addToast({
            body: translate("We couldn't process your request. Please try again."),
            messageType: "danger",
            isFocusAfterMount: true,
        });
    };

    const openSuccessHandler = () => {
        setIsOpenSuccess(true);
        setIsOpen(false);
    };

    const rejectHandler = () => {
        postBreadthRebateProgram({
            action: "Reject",
            soldToId: soldto?.Id,
            onSuccess: () => {
                setIsOpen(false);
            },
            onError: showErrorToast,
        });
    };

    const closeHandler = () => {
        setIsOpen(false);
    };

    const headline = (
        <Typography {...styles.headlineText}>
            {translate("You're Eligible to Earn Up to 2% Back")}
            <br />
            {translate("Breadth Rebate Program")}
        </Typography>
    );
  
    const viewProgramDetailsLink =
        danaBreadthRebateProgramFile &&
            danaBreadthRebateProgramFile !== "" ? (
            <Clickable href={danaBreadthRebateProgramFile} {...styles.detailsLink} target={"_blank"}>
                {translate("View Program Details")}
            </Clickable>
        ) : null;

    const description = (
        <Typography {...styles.descriptionText}>
            {translate("Earn up to 2% back on your annual purchases when you buy across eligible Dana product categories.")}
        </Typography>
    );
    const successDescription = (
        <Typography {...styles.descriptionText}>
            {translate("Your purchases now qualify for the Breadth Rebate Program.  Buy across eligible Dana product categories and earn up to 2% back on your annual purchases.")}
        </Typography>
    );
    return (
        <>
            <Modal
                {...styles.modal}
                isOpen={isOpen}
                handleClose={closeHandler}
                headline={headline}
                sizeVariant="medium"
            >
                {description}
                {viewProgramDetailsLink}
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button
                        {...styles.primaryButton}
                        variant="primary"
                        disabled={isSubmitting}
                        onClick={openSuccessHandler}
                    >
                        {translate("Activate Rebate Program")}
                    </Button>
                    <Button
                        {...styles.secondaryButton}
                        variant="secondary"
                        disabled={isSubmitting}
                        onClick={rejectHandler}
                    >
                        {translate("Not Now")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <Modal
                {...styles.modal}
                isOpen={isOpenSuccess}
                isCloseable={false}
                headline={headline}
                sizeVariant="medium"
            >
                {successDescription}
                {viewProgramDetailsLink}
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button
                        {...styles.primaryButton}
                        variant="primary"
                        onClick={acceptHandler}
                    >
                        {translate("Start Earning Now")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(BreadthRebateProgram));
